import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  prevStep,
  setStep,
  nextStep,
  addJointApplicationData,
} from "app/lib/store/slices/steps/loanTypesSteps";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InputField from "app/components/common/inputs/InputField";
import FormGroup from "app/components/common/inputs/FormGroup";
import SubTitle from "app/components/common/typography/SubTitle";
import InfoTitle from "app/components/common/typography/InfoTitle";
import { expenses } from "app/utils/applicant/types/types";

interface FormData {
  yourDetails: expenses;
  coBorrowerDetails: expenses;
}

const Expences: React.FC = () => {
  const dispatch = useDispatch();
  const applicationData = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      yourDetails: {
        totalExpenses: 0,
        monthlyRentExpenses: 0,
        privateSchooling: 0,
        privateInsurance: 0,
        childSupport: 0,
      },
      coBorrowerDetails: {
        totalExpenses: 0,
        monthlyRentExpenses: 0,
        privateSchooling: 0,
        privateInsurance: 0,
        childSupport: 0,
      },
    },
  });

  const onSubmit = (data: FormData) => {
    const { coBorrowerDetails, yourDetails } = data;

    const mapExpenses = (
      details: expenses,
      existingExpenses: { type: string; id?: string }[] = []
    ) =>
      [
        {
          type: "total-expense",
          amount: Number(details.totalExpenses) || 0,
        },
        {
          type: "monthly-rent",
          amount: Number(details.monthlyRentExpenses) || 0,
        },
        {
          type: "private-schooling",
          amount: Number(details.privateSchooling) || 0,
        },
        {
          type: "private-insurance",
          amount: Number(details.privateInsurance) || 0,
        },
        {
          type: "child-support",
          amount: Number(details.childSupport) || 0,
        },
      ].map((expense) => {
        const existing = existingExpenses.find((e) => e.type === expense.type);
        return {
          ...expense,
          id: existing?.id, // Preserve ID if it exists
        };
      });

    dispatch(
      addJointApplicationData({
        category: "YourDetails",
        data: {
          ...(applicationData?.YourDetails || {}),
          expenses: mapExpenses(
            yourDetails,
            applicationData?.YourDetails?.expenses || []
          ),
        },
      })
    );
    dispatch(
      addJointApplicationData({
        category: "Coborrower",
        data: {
          ...(applicationData?.Coborrower || {}),
          expenses: mapExpenses(
            coBorrowerDetails,
            applicationData?.Coborrower?.expenses || []
          ),
        },
      })
    );
    dispatch(nextStep());
  };

  useEffect(() => {
    const getAmountByType = (expenses: any[], type: string): number => {
      return expenses?.find((expense) => expense.type === type)?.amount || 0;
    };

    if (
      applicationData?.YourDetails?.expenses ||
      applicationData?.Coborrower?.expenses
    ) {
      reset({
        yourDetails: {
          totalExpenses: getAmountByType(
            applicationData?.YourDetails?.expenses || [],
            "total-expense"
          ),
          monthlyRentExpenses: getAmountByType(
            applicationData?.YourDetails?.expenses || [],
            "monthly-rent"
          ),
          privateSchooling: getAmountByType(
            applicationData?.YourDetails?.expenses || [],
            "private-schooling"
          ),
          privateInsurance: getAmountByType(
            applicationData?.YourDetails?.expenses || [],
            "private-insurance"
          ),
          childSupport: getAmountByType(
            applicationData?.YourDetails?.expenses || [],
            "child-support"
          ),
        },
        coBorrowerDetails: {
          totalExpenses: getAmountByType(
            applicationData?.Coborrower?.expenses || [],
            "total-expense"
          ),
          monthlyRentExpenses: getAmountByType(
            applicationData?.Coborrower?.expenses || [],
            "monthly-rent"
          ),
          privateSchooling: getAmountByType(
            applicationData?.Coborrower?.expenses || [],
            "private-schooling"
          ),
          privateInsurance: getAmountByType(
            applicationData?.Coborrower?.expenses || [],
            "private-insurance"
          ),
          childSupport: getAmountByType(
            applicationData?.Coborrower?.expenses || [],
            "child-support"
          ),
        },
      });
    }
  }, [applicationData, reset]);
  return (
    <>
      <ProgressBar progress={90} />
      <div className="w-full  sm:w-[70%] xl:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Expenses" />
        <SubTitle title="Please include all of your monthly expenses to the best of your knowledge to avoid rejection of your loan" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-5 flex-col md:flex-col xl:flex-row sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              <FormGroup>
                <InputField
                  label="Total monthly living expenses"
                  name="yourDetails.totalExpenses"
                  control={control}
                  type="number"
                  required={true}
                  placeholder="$0"
                  error={errors.yourDetails?.totalExpenses?.message}
                  className="flex-1"
                  rules={{
                    required: "Field is required",
                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Monthly expenses for entertainment and lifestyle (including dining out, shopping, vacations, personal care, etc.)"
                />
              </FormGroup>
              <FormGroup>
                <InputField
                  label="Monthly rent expenses"
                  name="yourDetails.monthlyRentExpenses"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={errors.yourDetails?.monthlyRentExpenses?.message}
                  className="flex-1"
                  rules={{
                    // required: "Field is required",
                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Your monthly rent. If you pay a mortgage, Don't include it here."
                />
              </FormGroup>
              <FormGroup>
                <InputField
                  label="Private schooling"
                  name="yourDetails.privateSchooling"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={errors.yourDetails?.privateSchooling?.message}
                  className="flex-1 "
                  rules={{
                    // required: "Field is required",

                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Tuition fees and regular costs for private schools."
                />
              </FormGroup>
              <FormGroup className="sm:items-end">
                <InputField
                  label="Private insurance"
                  name="yourDetails.privateInsurance"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={errors.yourDetails?.privateInsurance?.message}
                  className="flex-1 sm:mt-0 mt-5"
                  rules={{
                    // required: "Field is required",

                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Monthly payment for private health, life, or income insurance."
                />
                <InputField
                  label="Child Support"
                  name="yourDetails.childSupport"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={errors.yourDetails?.childSupport?.message}
                  className="flex-1 sm:mt-0 mt-5"
                  rules={{
                    // required: "Field is required",

                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Legally required or agreed child support payments."
                />
              </FormGroup>
            </div>
            <div className="flex-1 sm:mt-0 mt-5">
              <InfoTitle title="Co-borrower" />
              <FormGroup>
                <InputField
                  label="Total monthly living expenses"
                  name="coBorrowerDetails.totalExpenses"
                  control={control}
                  type="number"
                  required={true}
                  placeholder="$0"
                  error={errors.coBorrowerDetails?.totalExpenses?.message}
                  className="flex-1"
                  rules={{
                    required: "Field is required",
                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Monthly expenses for entertainment and lifestyle (including dining out, shopping, vacations, personal care, etc.)"
                />
              </FormGroup>
              <FormGroup>
                <InputField
                  label="Monthly rent expenses"
                  name="coBorrowerDetails.monthlyRentExpenses"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={errors.coBorrowerDetails?.monthlyRentExpenses?.message}
                  className="flex-1"
                  rules={{
                    // required: "Field is required",

                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Your monthly rent. If you pay a mortgage, Don't include it here."
                />
              </FormGroup>
              <FormGroup>
                <InputField
                  label="Private schooling"
                  name="coBorrowerDetails.privateSchooling"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={errors.coBorrowerDetails?.privateSchooling?.message}
                  className="flex-1"
                  rules={{
                    // required: "Field is required",

                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Tuition fees and regular costs for private schools."
                />
              </FormGroup>
              <FormGroup className="sm:items-end">
                <InputField
                  label="Private insurance"
                  name="coBorrowerDetails.privateInsurance"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={errors.coBorrowerDetails?.privateInsurance?.message}
                  className="flex-1 sm:mt-0 mt-5"
                  rules={{
                    // required: "Field is required",

                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Monthly payment for private health, life, or income insurance."
                />
                <InputField
                  label="Child Support"
                  name="coBorrowerDetails.childSupport"
                  control={control}
                  type="number"
                  required={false}
                  placeholder="$0"
                  error={errors.coBorrowerDetails?.childSupport?.message}
                  className="flex-1 sm:mt-0 mt-5"
                  rules={{
                    // required: "Field is required",

                    min: {
                      value: 0,
                      message: "Dependents value cannot be negative",
                    },
                    validate: (value: number) =>
                      value >= 0 || "Value cannot be negative",
                  }}
                  {...{ min: 0 }}
                  tooltip="Legally required or agreed child support payments."
                />
              </FormGroup>
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit">Next</ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default Expences;
